import React, { useState } from "react";
import {
  MessageBar,
  MessageBarType,
  Toggle,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import CustomerComboBox from "@/ui-controls/CustomerComboBox";
import { Grid } from "fluentui-react-grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  IntegrationDto,
  setupCustomerIntegrationSetting,
} from "@/api/integrations";

interface Props {
  integrationId?: number;
  integrations?: IntegrationDto[];
  onSave?: () => void;
  onClose?: () => void;
}

const NewCustomerSettingForm = (props: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const integrationOptions: IDropdownOption[] = (props.integrations || []).map(
    (i) => ({ key: i.id, text: `${i.code} - ${i.name}` })
  );

  const formik = useFormik({
    initialValues: {
      integrationId: props.integrationId ?? 0,
      customerId: "",
      usesMasterConfig: true,
      isActive: true,
    },
    validationSchema: Yup.object({
      integrationId: Yup.number()
        .required("Integration is required")
        .positive("Integration is required"),
      customerId: Yup.number()
        .required("Customer is required")
        .positive("Must be a positive number"),
    }),
    onSubmit: (values) => {
      setIsSaving(true);
      setErrorMessage("");
      setupCustomerIntegrationSetting({
        integrationId: values.integrationId,
        customerId: Number(values.customerId),
        usesMasterConfig: values.usesMasterConfig,
        isActive: values.isActive,
      })
        .then(
          () => {
            props.onSave?.();
          },
          (error) => {
            const data = error?.response?.data;
            setErrorMessage(
              data?.detail || "Failed to create customer setting."
            );
          }
        )
        .finally(() => setIsSaving(false));
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        {!props.integrationId && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <Dropdown
                label="Integration"
                placeholder="Select an integration"
                options={integrationOptions}
                selectedKey={formik.values.integrationId || undefined}
                onChange={(_, option) =>
                  formik.setFieldValue("integrationId", option?.key ?? 0)
                }
                disabled={isSaving}
                errorMessage={
                  formik.errors.integrationId && formik.touched.integrationId
                    ? formik.errors.integrationId
                    : undefined
                }
              />
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <CustomerComboBox
              selectedCustomerId={
                formik.values.customerId
                  ? Number(formik.values.customerId)
                  : undefined
              }
              onChange={(id) =>
                formik.setFieldValue("customerId", id ? String(id) : "")
              }
              disabled={isSaving}
              errorMessage={
                formik.errors.customerId && formik.touched.customerId
                  ? formik.errors.customerId
                  : undefined
              }
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <Toggle
              label="Uses Master Config"
              checked={formik.values.usesMasterConfig}
              onChange={(_, checked) =>
                formik.setFieldValue("usesMasterConfig", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <Toggle
              label="Active"
              checked={formik.values.isActive}
              onChange={(_, checked) =>
                formik.setFieldValue("isActive", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={() => props.onClose?.()}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

export default NewCustomerSettingForm;
